import type { OperatorMissionView } from '@groupe-reca/contracts'
import type { DetectionSetup } from '@/features/detection/application/DetectionRunner'
import type { LatLng } from '@/features/detection/domain/geometry'
import { detectionProfileSchema, type DetectionProfile } from '@/features/detection/domain/profile'

// Profil de détection et drapeaux d'une mission : figés par le serveur au démarrage (ADR-018, migration
// 20261031010000, contrats 0.4.0) et lus tels quels. Sans profil (ou profil OFF, ou invalide), la détection reste
// désactivée et l'écran garde son comportement manuel. Jamais de drapeau déduit d'une autre coordonnée.

export interface DetectionSource {
  /** Détection annoncée sur l'écran de départ (connue d'avance seulement pour les données fictives). */
  available: boolean
  setup: (view: OperatorMissionView) => DetectionSetup | null
}

type FrozenProfile = NonNullable<OperatorMissionView['mission']['detectionProfile']>

function parseFrozenProfile(frozen: FrozenProfile) {
  return detectionProfileSchema.safeParse({
    ...frozen.parameters,
    id: frozen.id,
    version: frozen.version,
    label: frozen.label,
    mode: frozen.mode,
    certified: frozen.certified,
  })
}

export function detectionSetupFromView(view: OperatorMissionView): DetectionSetup | null {
  const frozen = view.mission.detectionProfile
  if (!frozen || frozen.mode === 'OFF') return null
  const parsed = parseFrozenProfile(frozen)
  if (!parsed.success) return null
  return { profile: parsed.data, flags: flagsFromView(view) }
}

/** Drapeaux du snapshot de la mission, par résidence; `null` : résidence hors détection. */
export function flagsFromView(view: OperatorMissionView): Record<string, LatLng | null> {
  const flags: Record<string, LatLng | null> = {}
  for (const item of view.items) {
    const flag = item.snapshot.flag
    flags[item.id] = flag ? { lat: flag.latitude, lng: flag.longitude } : null
  }
  return flags
}

/** Profil figé par le serveur, même en mode OFF (base de la calibration locale); `null` s'il manque ou est invalide. */
export function serverProfileOf(view: OperatorMissionView): DetectionProfile | null {
  const frozen = view.mission.detectionProfile
  if (!frozen) return null
  const parsed = parseFrozenProfile(frozen)
  return parsed.success ? parsed.data : null
}

export function createSnapshotDetectionSource(available: boolean): DetectionSource {
  return { available, setup: detectionSetupFromView }
}

export type DetectionDiagnosis =
  | { status: 'READY'; withFlag: number; total: number }
  | { status: 'NO_PROFILE' | 'OFF'; withFlag: number; total: number }
  | { status: 'INVALID'; withFlag: number; total: number; issues: string[] }

/** Pourquoi la détection tourne ou non (diagnostic de calibration, temporaire). */
export function diagnoseDetection(view: OperatorMissionView): DetectionDiagnosis {
  const total = view.items.length
  const withFlag = view.items.filter((item) => item.snapshot.flag).length
  const frozen = view.mission.detectionProfile
  if (!frozen) return { status: 'NO_PROFILE', withFlag, total }
  if (frozen.mode === 'OFF') return { status: 'OFF', withFlag, total }
  const parsed = parseFrozenProfile(frozen)
  if (!parsed.success) {
    return {
      status: 'INVALID',
      withFlag,
      total,
      issues: parsed.error.issues.map((issue) => `${issue.path.join('.')} : ${issue.message}`),
    }
  }
  return { status: 'READY', withFlag, total }
}
